/**
 * Article Search Grid
 *
 * Powers the information centre article listing. Handles:
 *  - Tag pill filtering (click to apply, click again to deselect)
 *  - Free-text search via Shopify's /search endpoint
 *  - Load-more pagination
 *  - Mobile/desktop tag expand/collapse (show more hidden pills)
 *
 * Filtering & search use Shopify's Section Rendering API (?section_id=)
 * to fetch re-rendered section HTML and swap the grid in place without
 * a full page reload.
 */

import { debounce } from 'throttle-debounce';
import { SETTINGS, ARTICLE_SEARCH_GRID } from '../utils/constants';

// This module assumes a single Article Search Grid instance per page.
// If a merchant ever adds two, only the first is wired up. Re-init on
// `shopify:section:load` is handled by the template entry point.
const initArticleSearchGrid = () => {
  const container = document.querySelector('[data-article-search-grid]');
  if (!container) return;

  // Cache Liquid-rendered data and frequently-accessed elements once at init
  const sectionId = container.dataset.sectionId;
  const blogUrl = container.dataset.blogUrl;
  const statusResultsTemplate = container.dataset.statusResultsTemplate || '';
  const statusNoResults = container.dataset.statusNoResults || '';
  const searchInput = container.querySelector('[data-article-search-input]');
  const filterList = container.querySelector('[data-article-filter-list]');
  const expandBtn = container.querySelector('[data-tags-expand-btn]');
  const statusEl = container.querySelector('[data-article-status]');

  let tagsExpanded = false;

  // Tracks the most recent fetch so stale responses from earlier
  // requests never overwrite newer UI state.
  let activeController = null;

  // Locale-aware root path (e.g. "/fr/" on multilingual stores, "/" otherwise)
  const routesRoot = window.Shopify?.routes?.root || '/';

  // --- Fetch & render helpers ---

  /**
   * Fetches a Shopify page via the Section Rendering API and parses
   * the response into a Document so we can extract the re-rendered
   * grid/pagination markup. Throws on non-2xx so callers can surface
   * the failure instead of silently rendering a 404 page.
   *
   * Automatically aborts any previous in-flight request so that only
   * the most recent fetch can update the UI.
   */
  const fetchSectionHtml = async (url) => {
    if (activeController) activeController.abort();
    activeController = new AbortController();

    const separator = url.includes('?') ? '&' : '?';
    const fetchUrl = `${url}${separator}section_id=${sectionId}`;
    const response = await fetch(fetchUrl, { signal: activeController.signal });
    if (!response.ok) {
      throw new Error(`Section fetch failed (${response.status}): ${fetchUrl}`);
    }
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  };

  // --- Status region (screen reader announcements) ---

  /**
   * Updates the off-screen status region with a result count so screen
   * readers announce changes after a filter/search swap. Counts cards
   * directly from the current grid — no Liquid re-parse needed.
   */
  const updateStatus = () => {
    if (!statusEl) return;
    const count = container.querySelectorAll(ARTICLE_SEARCH_GRID.SELECTORS.ARTICLE_CARD).length;
    if (count === 0) {
      statusEl.textContent = statusNoResults;
    } else {
      statusEl.textContent = statusResultsTemplate.replace(ARTICLE_SEARCH_GRID.STATUS_COUNT_PLACEHOLDER, count);
    }
  };

  // --- Load more & grid rendering ---

  /**
   * Handles "Load more" clicks via event delegation — bound once to the
   * container, survives any grid re-render, and never needs to re-bind
   * itself. Appends new cards to the existing grid (to preserve scroll
   * position) then swaps the load-more block to reflect the next page.
   */
  const handleLoadMoreClick = async (e) => {
    const button = e.target.closest(ARTICLE_SEARCH_GRID.SELECTORS.LOAD_MORE_BTN);
    if (!button) return;

    e.preventDefault();

    // Visual loading state while the fetch is in flight. The classes
    // don't need an explicit removal on success — the button is replaced
    // wholesale when we swap the load-more block below. On failure or on
    // the last page (no new button) we clear them manually.
    button.classList.add(...ARTICLE_SEARCH_GRID.LOADING_CLASSES);

    try {
      const doc = await fetchSectionHtml(button.href);
      const newItems = doc.querySelectorAll(ARTICLE_SEARCH_GRID.SELECTORS.ARTICLE_CARD);
      const currentGrid = container.querySelector('[data-article-grid]');

      // Each card's outer <a> is what we want — data-article-card sits
      // inside the anchor, so walk up to find the wrapper we want to clone
      newItems.forEach((item) => {
        currentGrid.appendChild(item.closest('a').cloneNode(true));
      });

      // Replace the load-more block so it reflects the next page (or
      // disappears if we've hit the end). This also refreshes the
      // "Showing X of Y" count because the count text lives inside
      // the load-more block.
      const newLoadMore = doc.querySelector(ARTICLE_SEARCH_GRID.SELECTORS.LOAD_MORE);
      const currentLoadMore = container.querySelector(ARTICLE_SEARCH_GRID.SELECTORS.LOAD_MORE);
      if (currentLoadMore && newLoadMore) {
        currentLoadMore.innerHTML = newLoadMore.innerHTML;
      } else if (currentLoadMore) {
        // Last page reached — no new load-more block in the response.
        // Strip the loading classes so the button doesn't sit stuck at
        // 50% opacity (it'll be gone on the next filter swap anyway).
        button.classList.remove(...ARTICLE_SEARCH_GRID.LOADING_CLASSES);
      }

      updateStatus();
    } catch (err) {
      console.error('Error loading more articles:', err);
      button.classList.remove(...ARTICLE_SEARCH_GRID.LOADING_CLASSES);
    }
  };

  /**
   * Replaces the entire grid + pagination block with the fetched version.
   * Used when applying/removing filters and when running searches — the
   * entire result set changes, so we swap rather than append.
   *
   * We swap the whole `[data-article-grid-container]` rather than just the
   * inner grid, so that empty-state markup (a `<p>` with no grid element)
   * replaces the previous results cleanly.
   */
  const renderGrid = (doc) => {
    const newContainer = doc.querySelector(ARTICLE_SEARCH_GRID.SELECTORS.GRID_CONTAINER);
    const currentContainer = container.querySelector(ARTICLE_SEARCH_GRID.SELECTORS.GRID_CONTAINER);
    if (newContainer && currentContainer) {
      currentContainer.innerHTML = newContainer.innerHTML;
      updateStatus();
    }
  };

  // --- Tag filtering ---

  /**
   * Marks a single filter pill as active, clearing the active state
   * on all other pills. Pass `null` to clear active state entirely.
   *
   * The visual styling (background/border) is driven purely by the
   * `aria-pressed:` Tailwind variant on the `btn-filter-pill` utility,
   * so updating the ARIA attribute here automatically updates the look.
   */
  const setActiveTag = (button) => {
    if (!filterList) return;
    const buttons = filterList.querySelectorAll(ARTICLE_SEARCH_GRID.SELECTORS.FILTER_TAG);
    buttons.forEach((btn) => {
      btn.setAttribute('aria-pressed', btn === button ? 'true' : 'false');
    });
  };

  const clearActiveTag = () => setActiveTag(null);

  /**
   * Toggle-filter behaviour: clicking an inactive pill applies the
   * filter; clicking the same pill again deselects it and returns
   * to the unfiltered blog view.
   */
  const handleTagClick = async (button) => {
    // Any tag interaction should wipe an in-progress search so the
    // two filter modes don't fight each other
    if (searchInput) searchInput.value = '';

    const isAlreadyActive = button.getAttribute('aria-pressed') === 'true';
    const tag = button.dataset.filterTag;

    // Default to the unfiltered blog URL; only switch to a tagged URL
    // when we're applying (not removing) a filter
    let url = blogUrl;
    if (isAlreadyActive) {
      clearActiveTag();
    } else {
      setActiveTag(button);
      if (tag) {
        url = `${blogUrl}/tagged/${encodeURIComponent(tag)}`;
      }
    }

    try {
      const doc = await fetchSectionHtml(url);
      renderGrid(doc);
      // Keep the URL in sync so refresh/back-button behave as expected
      window.history.pushState({}, '', url);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error filtering articles:', err);
    }
  };

  // Event delegation on the container — a single listener handles
  // clicks on filter pills AND on the load-more button (which lives
  // inside the grid container and gets swapped on every render).
  container.addEventListener('click', (e) => {
    const filterBtn = e.target.closest(ARTICLE_SEARCH_GRID.SELECTORS.FILTER_TAG);
    if (filterBtn) {
      handleTagClick(filterBtn);
      return;
    }
    if (e.target.closest(ARTICLE_SEARCH_GRID.SELECTORS.LOAD_MORE_BTN)) {
      handleLoadMoreClick(e);
    }
  });

  // --- Search ---

  /**
   * Handles the debounced search input. Below ARTICLE_SEARCH_GRID.MIN_SEARCH_LENGTH we
   * treat the query as "empty" and reset the grid to show everything;
   * at or above it we hit Shopify's /search endpoint scoped to articles.
   */
  const handleSearch = async (query) => {
    if (!query || query.length < ARTICLE_SEARCH_GRID.MIN_SEARCH_LENGTH) {
      // Short query = treat as cleared: drop any active filter and
      // restore the full blog listing
      clearActiveTag();
      try {
        const doc = await fetchSectionHtml(blogUrl);
        renderGrid(doc);
        window.history.pushState({}, '', blogUrl);
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Error resetting articles:', err);
      }
      return;
    }

    // Searching overrides any tag filter — unpress whichever pill is active
    clearActiveTag();

    // Prefix-last tells Shopify to match partial words at the end of the
    // query, which gives better UX while typing.
    // Uses the locale-aware routes root so multilingual stores hit the
    // correct search endpoint.
    const searchUrl = `${routesRoot}search?q=${encodeURIComponent(query)}&type=article&options[prefix]=last`;

    try {
      const doc = await fetchSectionHtml(searchUrl);
      renderGrid(doc);
      // Push a blog-relative URL so hard refresh still loads the blog
      // template, but include ?q= so popstate can reconstruct the search.
      window.history.pushState({}, '', `${blogUrl}?q=${encodeURIComponent(query)}`);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error searching articles:', err);
    }
  };

  // Debounce so we don't fire a fetch on every keystroke
  const debouncedSearch = debounce(SETTINGS.SEARCH_DEBOUNCE_DELAY, (e) => {
    handleSearch(e.target.value.trim());
  });

  if (searchInput) {
    searchInput.addEventListener('input', debouncedSearch);
  }

  // --- Back/forward button support ---

  /**
   * Re-fetch whatever URL the browser navigates back/forward to so the
   * grid state stays in sync with the address bar. Also re-hydrates the
   * active filter pill from the `/tagged/` segment and the search input
   * from the `?q=` query param, so the controls match what the grid is
   * showing after a history nav.
   */
  window.addEventListener('popstate', async () => {
    try {
      // The address bar holds a blog-relative URL. If ?q= is present we
      // need to fetch from the real /search endpoint, not the blog path.
      const urlParams = new URLSearchParams(window.location.search);
      const queryParam = urlParams.get('q') || '';

      let fetchUrl;
      if (queryParam) {
        fetchUrl = `${routesRoot}search?q=${encodeURIComponent(queryParam)}&type=article&options[prefix]=last`;
      } else {
        fetchUrl = window.location.pathname + window.location.search;
      }

      const doc = await fetchSectionHtml(fetchUrl);
      renderGrid(doc);

      if (searchInput) searchInput.value = queryParam;

      // If there's an active search, no pill should be pressed
      if (queryParam) {
        clearActiveTag();
        return;
      }

      // Otherwise sync the active pill to whatever tag (if any) is in the URL.
      // Decode the URL segment so encoded tags match their data attributes.
      const tagMatch = window.location.pathname.match(/\/tagged\/([^/?]+)/);
      if (tagMatch && filterList) {
        const decodedTag = decodeURIComponent(tagMatch[1]);
        const activeBtn = filterList.querySelector(`[data-filter-tag="${decodedTag}"]`);
        if (activeBtn) {
          setActiveTag(activeBtn);
          return;
        }
      }
      clearActiveTag();
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error handling browser navigation:', err);
    }
  });

  // --- Tag expand/collapse ---

  /**
   * Shows/hides the overflow tag pills. Mobile and desktop each have
   * their own "hidden by default" set, so we toggle the two responsive
   * variants independently:
   *   - `max-lg:hidden` — hides on mobile, revealed on mobile expand
   *   - `lg:hidden` — hides on desktop, revealed on desktop expand
   */
  const toggleTags = () => {
    tagsExpanded = !tagsExpanded;

    container.querySelectorAll('[data-mobile-toggleable]').forEach((tag) => {
      tag.classList.toggle('max-lg:hidden', !tagsExpanded);
    });

    container.querySelectorAll('[data-desktop-toggleable]').forEach((tag) => {
      tag.classList.toggle('lg:hidden', !tagsExpanded);
    });

    if (expandBtn) {
      expandBtn.setAttribute('aria-expanded', tagsExpanded ? 'true' : 'false');
      // Only rotate the icon — leaving the button itself stationary
      const icon = expandBtn.querySelector('svg');
      if (icon) icon.classList.toggle('rotate-180', tagsExpanded);
    }

    // Desktop-only: lift the max-height cap when expanded so the list
    // can grow to its full wrapped height. Mobile has no cap to begin with.
    if (filterList) {
      filterList.classList.toggle('lg:max-h-27.5', !tagsExpanded);
      filterList.classList.toggle('lg:max-h-none', tagsExpanded);
    }
  };

  if (expandBtn) {
    expandBtn.addEventListener('click', toggleTags);
  }

  // --- Init ---

  // Populate the status region with the initial page's count so assistive
  // tech has something to announce if the user focuses it immediately.
  updateStatus();
};

export default initArticleSearchGrid;
